import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  Alert,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import type {
  RequestDto,
  UpdateStatusDto,
  UpdateRequestDto,
} from "../../../types/crm/types/request";
import requestService from "../../../services/CrmRequestService";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../../context/AuthContext";

interface RequestDetailProps {
  requestId: number;
  onStatusChange?: () => void;
}

const RequestDetail: React.FC<RequestDetailProps> = ({
  requestId,
  onStatusChange,
}) => {
  const [request, setRequest] = useState<RequestDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<number | "">("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateRequestDto>({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const { userToken } = useAuth();

  // Determinar el rol del usuario actual usando el perfil activo
  const activeProfile = userToken?.user.profiles.find(profile => profile.roleId === 7 || profile.roleId === 3);

  const userRole = activeProfile ? activeProfile.roleName : "";

  const statusOptions = [
    { id: 4, name: "Pendiente", color: "#FFA500" },
    { id: 6, name: "Aprobada", color: "#00FF00" },
    { id: 5, name: "Rechazada", color: "#FF0000" },
  ];

  useEffect(() => {
    const fetchRequestDetail = async () => {
      try {
        setLoading(true);
        const data = await requestService.getRequestById(requestId);

        if (data) {
          setRequest(data);
          setSelectedStatus(data.moduleStateId);

          // Inicializar el formulario de edición con los datos actuales
          setEditFormData({
            message: data.message,
            economicActivity: data.economicActivity,
            // Campos para solicitudes de compra
            paymentMethod: data.paymentMethod,
            availableFunds: data.availableFunds,
            financingNeeded: data.financingNeeded,
            // Campos para solicitudes de renta
            adults: data.adults,
            children: data.children,
            petType: data.petType,
            vehicleSize: data.vehicleSize,
            currentResidence: data.currentResidence,
            movingReason: data.movingReason,
          });
        } else {
          throw new Error("No se encontró la solicitud");
        }

        setError(null);
      } catch (err: any) {
        console.error("Error al obtener detalles de la solicitud:", err);
        setError(err.message || "Error al cargar los detalles de la solicitud");
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequestDetail();
    }
  }, [requestId]);

  const handleStatusChange = async (event: SelectChangeEvent<number | "">) => {
    const value = event.target.value;

    if (!request || value === "" || value === request.moduleStateId) return;

    const newStatus = Number(value);

    try {
      setStatusLoading(true);
      const updateData: UpdateStatusDto = {
        requestId: request.id,
        requestModuleStateId: newStatus,
      };

      const result = await requestService.updateRequestStatus(updateData);

      if (result.success) {
        if (result.updatedRequest) {
          // Usar directamente la solicitud actualizada devuelta por el servidor
          setRequest(result.updatedRequest);
          setSelectedStatus(result.updatedRequest.moduleStateId);
        } else {
          // Fallback: actualizar solo el estado si no tenemos la solicitud completa
          setRequest((prev) => {
            if (!prev) return null;
            // Actualizar también el statusCode y statusColor basado en el nuevo estado
            const newStatusOption = statusOptions.find(
              (option) => option.id === newStatus
            );
            return {
              ...prev,
              moduleStateId: newStatus,
              statusCode: newStatusOption?.name || prev.statusCode,
              statusColor: newStatusOption?.color || prev.statusColor,
            };
          });
          setSelectedStatus(newStatus);
        }

        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error("Error en handleStatusChange:", err);
      setError(err.message || "Error al actualizar el estado");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleEditFormChange = (field: keyof UpdateRequestDto, value: any) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = async () => {
    if (!request) return;

    try {
      setUpdateLoading(true);
      const result = await requestService.updateRequest(
        request.id,
        editFormData
      );

      if (result.success) {
        // Actualizar la solicitud en el estado local
        setRequest((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            ...editFormData,
          };
        });

        // Cerrar el diálogo
        setOpenEditDialog(false);

        // Mostrar mensaje de éxito
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.message || "Error al actualizar la solicitud");
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {error}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            ID de solicitud: {requestId}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!request) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Alert severity="warning" sx={{ width: "100%" }}>
          No se encontró la solicitud
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            ID de solicitud: {requestId}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Determinar si el usuario puede cambiar el estado (solo brokers)
  const canChangeStatus = userRole === "Broker";

  // Determinar si el usuario puede editar la solicitud (solo clientes)
  const canEditRequest = userRole === "Cliente";

  // Convertir modeId a número para comparaciones
  const modeIdAsNumber =
    typeof request.modeId === "string"
      ? Number.parseInt(request.modeId, 10)
      : request.modeId;

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Encabezado con información de la propiedad */}
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h5" component="h2">
              Solicitud #{request.id}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={request.statusCode}
                sx={{
                  backgroundColor: request.statusColor || "#999",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              />
              {canEditRequest && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleOpenEditDialog}
                  size="small"
                >
                  Editar
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="caption" display="block" color="text.secondary">
            Creada el {new Date(request.createdDate).toLocaleDateString()} a las{" "}
            {new Date(request.createdDate).toLocaleTimeString()}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Información de la propiedad */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de la Propiedad
              </Typography>

              <Box
                sx={{
                  height: 200,
                  backgroundImage: `url(${
                    request.propertyImage || "/placeholder-property.jpg"
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: 1,
                  mb: 2,
                }}
              />

              <Typography variant="subtitle1" fontWeight="bold">
                {request.propertyTitle}
              </Typography>

              <Typography variant="body2" gutterBottom>
                {request.propertyAddress}
              </Typography>

              <Typography variant="h6" color="primary" fontWeight="bold">
                ${(request.propertyPrice || 0).toLocaleString()}
              </Typography>

              <Chip
                label={modeIdAsNumber === 1 ? "Venta" : "Renta"}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Información del solicitante */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Solicitante
              </Typography>

              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "center", sm: "flex-start" }}
                gap={2}
                mb={2}
              >
                <Avatar
                  src={request.userProfileImage || "/placeholder-user.jpg"}
                  sx={{ width: 64, height: 64 }}
                />
                <Box textAlign={{ xs: "center", sm: "left" }} width="100%">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {request.userFullName}
                  </Typography>
                  <Typography variant="body2">{request.userEmail}</Typography>
                  <Typography variant="body2">{request.userPhone}</Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Actividad Económica:
              </Typography>
              <Typography variant="body2" paragraph>
                {request.economicActivity || "No especificada"}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Mensaje:
              </Typography>
              <Typography variant="body2">
                {request.message || "Sin mensaje"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles específicos según el tipo de solicitud */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {modeIdAsNumber === 1
                  ? "Detalles de Compra"
                  : "Detalles de Renta"}
              </Typography>

              {modeIdAsNumber === 1 ? (
                // Detalles de compra
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">Método de Pago:</Typography>
                    <Typography variant="body2">
                      {request.paymentMethod || "No especificado"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">
                      Fondos Disponibles:
                    </Typography>
                    <Typography variant="body2">
                      $
                      {request.availableFunds?.toLocaleString() ||
                        "No especificado"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">
                      Necesita Financiamiento:
                    </Typography>
                    <Typography variant="body2">
                      {request.financingNeeded ? "Sí" : "No"}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                // Detalles de renta
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="subtitle2">Adultos:</Typography>
                    <Typography variant="body2">
                      {request.adults || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="subtitle2">Niños:</Typography>
                    <Typography variant="body2">
                      {request.children || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Mascotas:</Typography>
                    <Typography variant="body2">
                      {request.petType || "Ninguna"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      Tamaño de Vehículo:
                    </Typography>
                    <Typography variant="body2">
                      {request.vehicleSize || "No especificado"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      Residencia Actual:
                    </Typography>
                    <Typography variant="body2">
                      {request.currentResidence || "No especificado"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      Motivo de Mudanza:
                    </Typography>
                    <Typography variant="body2">
                      {request.movingReason || "No especificado"}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Acciones - Cambio de estado (solo para brokers) */}
        {canChangeStatus && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Acciones
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="status-select-label">
                      Estado de la Solicitud
                    </InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={selectedStatus}
                      label="Estado de la Solicitud"
                      onChange={handleStatusChange}
                      disabled={statusLoading}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          <Box display="flex" alignItems="center">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: option.color,
                                mr: 1,
                              }}
                            />
                            {option.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {statusLoading && <CircularProgress size={24} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Diálogo de edición para clientes */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Editar Solicitud</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Campos comunes */}
            <Grid item xs={12}>
              <TextField
                label="Mensaje"
                multiline
                rows={4}
                fullWidth
                value={editFormData.message || ""}
                onChange={(e) =>
                  handleEditFormChange("message", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Actividad Económica"
                fullWidth
                value={editFormData.economicActivity || ""}
                onChange={(e) =>
                  handleEditFormChange("economicActivity", e.target.value)
                }
              />
            </Grid>

            {/* Campos específicos según el tipo de solicitud */}
            {modeIdAsNumber === 1 ? (
              // Campos para solicitud de compra
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Método de Pago"
                    fullWidth
                    value={editFormData.paymentMethod || ""}
                    onChange={(e) =>
                      handleEditFormChange("paymentMethod", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Fondos Disponibles"
                    type="number"
                    fullWidth
                    value={editFormData.availableFunds || ""}
                    onChange={(e) =>
                      handleEditFormChange(
                        "availableFunds",
                        Number(e.target.value)
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Necesita Financiamiento</InputLabel>
                    <Select
                      value={editFormData.financingNeeded ? "true" : "false"}
                      label="Necesita Financiamiento"
                      onChange={(e) =>
                        handleEditFormChange(
                          "financingNeeded",
                          e.target.value === "true"
                        )
                      }
                    >
                      <MenuItem value="true">Sí</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              // Campos para solicitud de renta
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Adultos"
                    type="number"
                    fullWidth
                    value={editFormData.adults || ""}
                    onChange={(e) =>
                      handleEditFormChange("adults", Number(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Niños"
                    type="number"
                    fullWidth
                    value={editFormData.children || ""}
                    onChange={(e) =>
                      handleEditFormChange("children", Number(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tipo de Mascota"
                    fullWidth
                    value={editFormData.petType || ""}
                    onChange={(e) =>
                      handleEditFormChange("petType", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tamaño de Vehículo"
                    fullWidth
                    value={editFormData.vehicleSize || ""}
                    onChange={(e) =>
                      handleEditFormChange("vehicleSize", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Residencia Actual"
                    fullWidth
                    value={editFormData.currentResidence || ""}
                    onChange={(e) =>
                      handleEditFormChange("currentResidence", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Motivo de Mudanza"
                    fullWidth
                    value={editFormData.movingReason || ""}
                    onChange={(e) =>
                      handleEditFormChange("movingReason", e.target.value)
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseEditDialog}
            fullWidth={window.innerWidth < 600}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveChanges}
            variant="contained"
            color="primary"
            disabled={updateLoading}
            fullWidth={window.innerWidth < 600}
          >
            {updateLoading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RequestDetail;
